interface TokenGeneratorOptions {
  /** Auth0 API to mint the token for; defaults to the Superblocks API. */
  audience?: string;
  ignoreCache?: boolean;
  /** Space-separated scopes to request alongside the audience. */
  scope?: string;
}

// Our callers ask for a fresh token with `ignoreCache`, and for a token for a
// different Auth0 API with `audience` and `scope`. The SDK spells those
// `cacheMode: "off"` and `authorizationParams`, and treats a missing cacheMode
// as "use the cache". Keeping the translation here means both the app and the
// dashboard wrapper hand the SDK the same argument, and that nothing a caller
// sets gets dropped on the way through - a token minted for the wrong audience
// is only rejected later, by whichever API it is sent to.
export const toGetTokenSilentlyOptions = (options?: TokenGeneratorOptions) => {
  // Each key is spread in only when the caller set it. The SDK merges
  // `authorizationParams` over the client's own, so an explicit `undefined`
  // would erase the default audience instead of leaving it alone.
  const translated = {
    ...(options?.ignoreCache && { cacheMode: "off" as const }),
    ...((options?.audience || options?.scope) && {
      authorizationParams: {
        ...(options.audience && { audience: options.audience }),
        ...(options.scope && { scope: options.scope }),
      },
    }),
  };
  return Object.keys(translated).length > 0 ? translated : undefined;
};

class AuthProvider {
  private static instance: AuthProvider;
  private tokenGenerator?: (
    options?: TokenGeneratorOptions,
  ) => Promise<string | undefined>;

  private constructor() {}

  setTokenGenerator(
    tokenGenerator: (
      options?: TokenGeneratorOptions,
    ) => Promise<string | undefined>,
  ) {
    this.tokenGenerator = tokenGenerator;
  }

  removeTokenGenerator() {
    this.tokenGenerator = undefined;
  }

  async generateToken(options?: TokenGeneratorOptions) {
    if (!this.tokenGenerator) {
      throw new Error("tokenGenerator accessed without initialization");
    }
    const token = await this.tokenGenerator(options);
    // getTokenSilently resolves undefined when Auth0 decides the session is
    // over. Without this check the
    // undefined reaches callers that build a "Bearer ${token}" header and only
    // fails later as an opaque 401.
    if (typeof token !== "string" || token.length === 0) {
      throw new Error("Auth0 returned an empty token");
    }
    return token;
  }

  async generateFreshToken() {
    return await this.generateToken({ ignoreCache: true });
  }

  ready(): boolean {
    return !!this.tokenGenerator;
  }

  public static get Instance() {
    return this.instance || (this.instance = new this());
  }
}

export default AuthProvider.Instance;
